/**
 * HomeWizard v2 Battery Group — group-level telemetry display.
 *
 * Shared between the Devices detail panel (when a battery-group device
 * is selected) and the dedicated Battery page. The two surfaces render
 * the same metrics: latest live power_w (with charging/discharging/idle
 * direction inferred from the sign), target_power_w, mode,
 * battery_count, max_consumption_w, max_production_w, and the two
 * permission badges (charge_allowed / discharge_allowed).
 *
 * Per Sprint 7.3 scope this component deliberately does NOT show:
 *   - per-battery state of charge
 *   - cycle counts
 *   - daily charge / discharge kWh
 *   - autonomy contribution
 * Those land in a later sprint when the underlying metrics start
 * flowing from the bridge.
 *
 * The component is purely presentational — it takes a ``readings`` array
 * (latest readings for the battery group device) and renders.
 */
import React from "react";
import {
  Activity, ArrowDownToLine, ArrowUpFromLine, BatteryCharging, Gauge, ShieldCheck, Target,
} from "lucide-react";
import { fmt, fmtInt } from "../../lib/format";

export function pickLatest(readings, metric) {
  for (const r of readings) {
    if (r.metric === metric && r.value != null) return r;
  }
  return null;
}

export function MetricTile({ Icon, label, value, unit, accent, testid }) {
  return (
    <div className="flex flex-col gap-1" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-muted-foreground">
        <Icon className="w-3 h-3" style={{ color: accent }} />
        <span className="label-tiny">{label}</span>
      </div>
      <div className="font-heading text-2xl font-semibold tabular-nums" style={{ color: accent }}>
        {value}
        {unit && <span className="text-xs text-muted-foreground ml-1 font-normal">{unit}</span>}
      </div>
    </div>
  );
}

export function BoolBadge({ on, label, testid }) {
  return (
    <div className="flex items-center gap-1.5" data-testid={testid}>
      <ShieldCheck className="w-3 h-3" style={{ color: on ? "#10B981" : "#71717A" }} />
      <span className="label-tiny">{label}</span>
      <span
        className="ml-1 inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded"
        style={{
          color: on ? "#10B981" : "#71717A",
          backgroundColor: on ? "#10B98115" : "#71717A15",
          border: `1px solid ${on ? "#10B98130" : "#71717A30"}`,
        }}
      >
        {on ? "Allowed" : "Blocked"}
      </span>
    </div>
  );
}

/**
 * Map a numeric power_w reading to a {direction, accent} pair.
 *   power > 0  → charging (the battery is consuming energy from the grid/PV)
 *   power < 0  → discharging (the battery is delivering energy to the home)
 *   power == 0 → idle
 * Returns "Unknown" + neutral colour when the value is missing/non-numeric.
 *
 * Exported so the battery page and tests can call the helper directly.
 */
export function batteryDirection(powerVal) {
  if (typeof powerVal !== "number" || Number.isNaN(powerVal)) {
    return { label: "Unknown", accent: "#71717A", isCharging: false, isDischarging: false };
  }
  if (powerVal > 0) return { label: "Charging", accent: "#10B981", isCharging: true, isDischarging: false };
  if (powerVal < 0) return { label: "Discharging", accent: "#F59E0B", isCharging: false, isDischarging: true };
  return { label: "Idle", accent: "#71717A", isCharging: false, isDischarging: false };
}

export default function HomeWizardBatteryGroupDetail({ readings, testid = "device-detail-battery-group" }) {
  const power = pickLatest(readings, "power_w");
  const target = pickLatest(readings, "target_power_w");
  const maxIn = pickLatest(readings, "max_consumption_w");
  const maxOut = pickLatest(readings, "max_production_w");
  const count = pickLatest(readings, "battery_count");
  const mode = pickLatest(readings, "mode");
  const chargeAllowed = pickLatest(readings, "charge_allowed");
  const dischargeAllowed = pickLatest(readings, "discharge_allowed");

  // Positive power = charging; negative = discharging.
  const dir = batteryDirection(power?.value);

  return (
    <div data-testid={testid}>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
        <MetricTile
          Icon={BatteryCharging}
          label="Power"
          value={power ? fmt(Math.abs(power.value), 1) : "—"}
          unit="W"
          accent={dir.accent}
          testid="battery-tile-power"
        />
        <MetricTile
          Icon={Target}
          label="Target power"
          value={target ? fmt(target.value, 1) : "—"}
          unit="W"
          accent="#3B82F6"
          testid="battery-tile-target"
        />
        <MetricTile
          Icon={Activity}
          label="Mode"
          value={mode?.unit ? mode.unit : "—"}
          unit=""
          accent="#FAFAFA"
          testid="battery-tile-mode"
        />
        <MetricTile
          Icon={Gauge}
          label="Battery count"
          value={count ? fmtInt(count.value) : "—"}
          unit=""
          accent="#FAFAFA"
          testid="battery-tile-count"
        />
        <MetricTile
          Icon={ArrowDownToLine}
          label="Max consumption"
          value={maxIn ? fmtInt(maxIn.value) : "—"}
          unit="W"
          accent="#E11D48"
          testid="battery-tile-max-consumption"
        />
        <MetricTile
          Icon={ArrowUpFromLine}
          label="Max production"
          value={maxOut ? fmtInt(maxOut.value) : "—"}
          unit="W"
          accent="#10B981"
          testid="battery-tile-max-production"
        />
      </div>
      <div className="mt-4 flex flex-wrap gap-4 pt-4 border-t border-border" data-testid="battery-permissions">
        <BoolBadge on={chargeAllowed?.value === 1} label="Charge" testid="battery-charge-allowed" />
        <BoolBadge on={dischargeAllowed?.value === 1} label="Discharge" testid="battery-discharge-allowed" />
        {power && (
          <span className="text-[11px] text-muted-foreground ml-auto" data-testid="battery-direction-label">
            Direction: <span className="text-foreground" style={{ color: dir.accent }}>{dir.label}</span>
          </span>
        )}
      </div>
    </div>
  );
}
